/**
 * Run assessment — the action the seven question-screens never had.
 *
 * Scope + standards + as-of + evidence-period (sealed bundles) selection.
 * The sentence in the footer is the freeze ruling made visible at exactly the
 * moment a user would otherwise assume a re-run updates what they're looking
 * at: A RUN PRODUCES A NEW SEALED REPORT AND NEVER MODIFIES A PRIOR ONE.
 * A test pins the sentence; the backend pins the behavior structurally.
 */
import React, { useEffect, useState } from 'react';
import api, { errorDetail } from '../../services/api';
import { fmtDate } from '../../utils/complianceFormat';

const STANDARDS = [
  ['NERC-CIP-015', 'CIP-015-1 (INSM)'],
  ['NERC-CIP-005', 'CIP-005-7 P1.2 (ESP / EAP transit)'],
  ['NERC-CIP-010', 'CIP-010-4 R1.1 (change authorization)'],
];

export default function RunAssessmentModal({ onClose, onDone }) {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState('');
  const [bundles, setBundles] = useState([]);
  const [picked, setPicked] = useState([]);
  const [standards, setStandards] = useState(STANDARDS.map(([k]) => k));
  const [asOf, setAsOf] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get('/ce/sites').then(({ data }) => {
      const list = data.sites || data || [];
      setSites(list);
      if (list.length) setSiteId(list[0].id);
    }).catch((err) => setError(errorDetail(err, 'Sites could not be loaded.')));
  }, []);

  useEffect(() => {
    if (!siteId) return;
    // Stale-response guard: switching sites while a fetch is in flight must
    // never pair one site's SCOPE with another site's EVIDENCE — a report
    // sealed from that pairing would be wrong, not just confusing. (Found by
    // the Playwright run racing the site select.)
    let stale = false;
    api.get(`/ce/sites/${siteId}/bundles`).then(({ data }) => {
      if (stale) return;
      const list = (data.bundles || data || []).filter(
        (b) => b.evidence_class === 'SEALED_EVIDENCE' || b.evidence_class === 'PARTIAL_REVIEW');
      setBundles(list);
      setPicked(list.map((b) => b.id));
    }).catch((err) => { if (!stale) setError(errorDetail(err, 'Sealed evidence could not be listed.')); });
    return () => { stale = true; };
  }, [siteId]);

  const toggle = (list, setList, v) =>
    setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const site = sites.find((s) => s.id === siteId);
      const { data } = await api.post('/skills/compliance/run', {
        system_ids: [siteId],
        standards,
        as_of: asOf || null,
        bundle_ids: picked,
        entity_name: site?.site_name || 'Deployment',
      });
      setResult(data);
      if (onDone) onDone(data);
    } catch (err) {
      setError(errorDetail(err, 'The run did not complete.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="run-modal">
      <div className="bg-gray-800 border border-gray-600 rounded-xl p-6 w-full max-w-xl text-gray-100 max-h-[90vh] overflow-y-auto">
        <h3 className="text-white font-semibold mb-1">Run assessment</h3>
        <p data-testid="run-freeze-sentence" className="text-xs text-gray-300 border-l-2 border-gray-500 pl-2 mb-4">
          A run produces a new sealed report and never modifies a prior one.
        </p>

        {!result && (
          <>
            <label className="block text-xs text-gray-400 mb-1">Scope</label>
            <select value={siteId} onChange={(e) => setSiteId(e.target.value)}
                    className="w-full bg-gray-900 border border-gray-600 rounded-lg p-2 text-sm mb-3">
              {sites.map((s) => <option key={s.id} value={s.id}>{s.site_name}</option>)}
            </select>

            <label className="block text-xs text-gray-400 mb-1">Standards</label>
            <div className="mb-3 space-y-1">
              {STANDARDS.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={standards.includes(k)}
                         onChange={() => toggle(standards, setStandards, k)} />
                  {label}
                </label>
              ))}
            </div>

            <label className="block text-xs text-gray-400 mb-1">
              Evidence period (sealed packets — the run cites only sealed evidence)
            </label>
            <div className="mb-3 space-y-1 max-h-40 overflow-y-auto border border-gray-700 rounded-lg p-2">
              {bundles.length === 0 && <div className="text-xs text-gray-500">No sealed evidence for this scope.</div>}
              {bundles.map((b) => (
                <label key={b.id} className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={picked.includes(b.id)}
                         onChange={() => toggle(picked, setPicked, b.id)} />
                  {fmtDate(b.period_start)} → {fmtDate(b.period_end)}
                  <span className="text-gray-500 font-mono">{b.id.slice(0, 8)}</span>
                </label>
              ))}
            </div>

            <label className="block text-xs text-gray-400 mb-1">Assess as of (optional, ISO — defaults to now)</label>
            <input value={asOf} onChange={(e) => setAsOf(e.target.value)}
                   placeholder="2026-08-20T19:00:00"
                   className="w-full bg-gray-900 border border-gray-600 rounded-lg p-2 text-sm mb-4" />

            {error && <div className="text-red-300 text-xs mb-2">{error}</div>}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border border-gray-600 text-gray-300">Cancel</button>
              <button data-testid="run-submit" onClick={submit}
                      disabled={busy || !siteId || standards.length === 0 || picked.length === 0}
                      className="px-3 py-1.5 text-sm rounded-lg bg-blue-700 text-white disabled:opacity-40 disabled:cursor-not-allowed">
                {busy ? 'Running…' : 'Run and seal a new report'}
              </button>
            </div>
          </>
        )}

        {result && (
          <div data-testid="run-result">
            <div className="text-sm text-green-300 mb-2">
              New sealed report {result.report_id.slice(0, 8)} · as of {result.assessment_as_of}
            </div>
            <div className="text-xs text-gray-300 space-y-1 mb-3">
              {result.determinations.map((d) => (
                <div key={d.requirement_id} className="font-mono">
                  {d.requirement_id}: {d.determination}
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 mb-3">{result.note}</p>
            <div className="flex justify-end">
              <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border border-gray-600 text-gray-300">Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
